import { FormlySelectOption } from '@ngx-formly/core/select/select-options.pipe';
import {
  concat,
  defaults,
  filter,
  find,
  flow,
  get,
  identity,
  includes,
  isBoolean,
  isEqual,
  isNumber,
  map,
  reject,
  values,
  xor,
} from 'lodash';

import { InjectionToken } from '@angular/core';
import { Partners } from '@lockton-apps/shared/interfaces';
import { FormlyExtension, FormlyFieldConfig } from '@ngx-formly/core';
import {
  combineLatest,
  distinctUntilChanged,
  map as rxMap,
  Observable,
  of,
  shareReplay,
} from 'rxjs';
import { CobMetaService } from './cob-meta.service';

export interface CobMeta<TDerived = any> {
  id: string;
  title: string;
  description?: string;
  department?: string;
  icon: string;
  color: string;
  externalLink?: string;
  misc?: any;
  mapTo?: TDerived;
  offline?: boolean;
  code?: string; //this should be required for all COBs
  type: 'policy' | 'addon' | 'bolton';
}

export enum PncClassOfBusinessId {
  PropertyDamage = 1843,
  BusinessInterruption = 1880,
  Money = 1866,
  EmployersLiability = 1829,
  PublicProductsLiability = 1830,
  MotorFleet = 1852,
  Computer = 1867,
  ContractWorksAndPlant = 1895,
  MarineCargo = 1921,
  GoodsInTransitFreightLiability = 1924,
  EngineeringInsuranceInspection = 1873,
  ManagementLiability = 1836,
  CyberLiability = 1850,
  Crime = 1901,
  PI = 1848,
  GroupPersonalAccidentTravel = 1913,
  LegalExpenses = 1897,
  DeteriorationOfStock = 1876,
  AirsideLiability = 1865,
  ProductContamination = 1838,
  RiskManagementPractices = 1863,
}

export enum LsosClassOfBusinessId {
  LSOS = 466,
  Cyber = 483,
  OfficeGuardian = 494,
  Crime = 493,
  ManagementLiability = 2374,
  PISolicitorsExcess = 467,
}

export enum NorwayClassOfBusinessId {
  ProfessionalIndemnity = 1541,
  TravelBusiness = 1519,
  Office = 1520,
  OffWorkAccident = 1521,
  WorkersCompensation = 1522,
  FamilyAccident = 1523,
  FamilyTravel = 1524,
  SickLeave = 1525,
  GroupLife = 1526,
  HealthPrivate = 2156,
  HealthBusiness = 1555,
  // Unconfirmed PI addons
  ProfessionalLiability = 7001,
  PropertyDamage = 7002,
  DirectorsLiability = 7003,
  Crime = 7004,
  Cyber = 7005,
}

export enum AccaClassOfBusinessId {
  ProfessionalIndemnity = 629,
  EmployersPublicAndProductLiability = 0, // Virtual/combined product
  EmployersLiability = 609,
  PublicAndProductLiability = 610,
  GroupPersonalAccident = 2157,
  ManagementLiability = 615,
  Cyber = 631,
  OfficeCombined = 2158,
}

export enum AccaOverClassOfBusinessId {
  NationalAccountants = 2275,
  NationalAccountantsAcca = 2279,
  ProfessionsAccountants = 337,
}

export enum ConstructionClassOfBusinessId {
  // A&E
  ProfessionsArchitects = 2264,
  ProfessionsEngineers = 2266,
  NationalArchitects = 2291,
  NationalEngineers = 2293,
  // D&C
  ProfessionsDesignAndConstruction = 2268,
  ProfessionsPropertyManagers = 2269, // also surveyors
  NationalDesignAndConstruction = 2296,
  NationalPropertyManagers = 2299, // also surveyors
  // Surveyors
  ProfessionsSurveyors = 2265,
  ProfessionsEstateAgent = 2267,
  NationalSurveyors = 2292,
  NationalEstateAgent = 2294,
  NationalSurveyorsRics = 2300,
}

export enum RegulatoryClassOfBusinessId {
  AccountantsIfa = 2277,
  RegulatoryFinancialServices = 2315,
  RegulatoryMortgageAdvisors = 2317,
  RegulatoryInsuranceBrokers = 2311,
  RegulatoryEnOIfa = 2312,
  RegulatoryLloyds = 2316,
  RegulatoryUnderwriterAgentsAndManagers = 2318,
}

export enum EnwClassOfBusinessId {
  PI = 676,
  PI2 = 335,
}

export enum MidMarketOfflineClassOfBusinessId {
  EmployersLiability = 11001,
  PublicAndProductLiability = 11002,
  ManagementLiability = 11003,
  GroupPersonalAccident = 11004,
  Cyber = 11005,
  OfficeCombined = 11006,
  Crime = 11007,
  EmployeeBenefits = 11008,
}

/** @deprecated this now falls under acca over */
const combinedAccaClassOfBusinesses = concat(
  filter(values(AccaClassOfBusinessId), isNumber),
  filter(values(AccaOverClassOfBusinessId), isNumber)
);

const combinedNorwayClassOfBusinesses = reject(
  filter(values(NorwayClassOfBusinessId), isNumber),
  (cob) =>
    includes(
      [
        NorwayClassOfBusinessId.ProfessionalLiability,
        NorwayClassOfBusinessId.PropertyDamage,
        NorwayClassOfBusinessId.DirectorsLiability,
        NorwayClassOfBusinessId.Crime,
        NorwayClassOfBusinessId.Cyber,
      ],
      cob
    )
);

export const PartnerClassOfBusinesses = {
  [Partners.LSOS]: filter(values(LsosClassOfBusinessId), isNumber),
  [Partners.NORWAY]: combinedNorwayClassOfBusinesses,
  [Partners.NORWAY_PI]: combinedNorwayClassOfBusinesses,
  [Partners.PNC]: filter(values(PncClassOfBusinessId), isNumber),
  [Partners.ACCA]: combinedAccaClassOfBusinesses, // @deprecated this now falls under ACCA_OVER
  [Partners.ACCA_OVER]: filter(values(AccaOverClassOfBusinessId), isNumber),
  [Partners.CONSTRUCTION]: filter(
    values(ConstructionClassOfBusinessId),
    isNumber
  ),
  [Partners.REGULATORY]: filter(values(RegulatoryClassOfBusinessId), isNumber),
  [Partners.ENW]: filter(values(EnwClassOfBusinessId), isNumber),
};

export type ClassOfBusinessId =
  | LsosClassOfBusinessId
  | NorwayClassOfBusinessId
  | AccaClassOfBusinessId
  | AccaOverClassOfBusinessId
  | PncClassOfBusinessId
  | ConstructionClassOfBusinessId
  | RegulatoryClassOfBusinessId
  | EnwClassOfBusinessId;

export const PNC_COB_META_DATA: Record<PncClassOfBusinessId, CobMeta> = {
  [PncClassOfBusinessId.PropertyDamage]: {
    id: 'property-damage',
    title: $localize`Property Damage`,
    icon: 'property_damage_thin',
    color: 'pink',
    code: '155',
    type: 'policy',
  },
  [PncClassOfBusinessId.BusinessInterruption]: {
    id: 'business-interruption',
    title: $localize`Business Interruption`,
    icon: 'business_interruption_thin',
    color: 'blue',
    code: '333',
    type: 'policy',
  },
  [PncClassOfBusinessId.Money]: {
    id: 'money',
    title: $localize`Money`,
    icon: 'financial_thin',
    color: 'green',
    code: '301',
    type: 'policy',
  },
  [PncClassOfBusinessId.EmployersLiability]: {
    id: 'employers-liability',
    title: $localize`Employers Liability`,
    icon: 'business_accident_health_thin',
    color: 'yellow',
    code: '111',
    type: 'policy',
  },
  [PncClassOfBusinessId.PublicProductsLiability]: {
    id: 'public-products-liability',
    title: $localize`PublicProductsLiability`,
    icon: 'family_accident_health_thin',
    color: 'orange',
    code: '112',
    type: 'policy',
  },
  [PncClassOfBusinessId.MotorFleet]: {
    id: 'motor-fleet',
    title: $localize`Motor Fleet`,
    icon: 'motorfleet_thin',
    color: 'red',
    code: '203',
    type: 'policy',
  },
  [PncClassOfBusinessId.Computer]: {
    id: 'computer',
    title: $localize`Computer`,
    icon: 'computer_thin',
    color: 'yellow',
    code: '303',
    type: 'policy',
  },
  [PncClassOfBusinessId.ContractWorksAndPlant]: {
    id: 'contract-works-and-plant',
    title: $localize`Contract Works and Plant`,
    icon: 'contract_thin',
    color: 'yellow',
    code: '393',
    type: 'policy',
  },
  [PncClassOfBusinessId.MarineCargo]: {
    id: 'marine-cargo',
    title: $localize`Marine Cargo`,
    icon: 'marine_thin',
    color: 'blue',
    code: '529',
    type: 'policy',
  },
  [PncClassOfBusinessId.GoodsInTransitFreightLiability]: {
    id: 'goods-in-transit-freight-liability',
    title: $localize`Goods in Transit Freight Liability`,
    icon: 'transit_thin',
    color: 'pink',
    code: '552',
    type: 'policy',
  },
  [PncClassOfBusinessId.EngineeringInsuranceInspection]: {
    id: 'engineering-insurance-inspection',
    title: $localize`Engineering Insurance Inspection`,
    icon: 'engineering_thin',
    color: 'orange',
    code: '311',
    type: 'policy',
  },
  [PncClassOfBusinessId.ManagementLiability]: {
    id: 'management-liability',
    title: $localize`Management Liability`,
    icon: 'dando_thin',
    color: 'pink',
    code: '124',
    type: 'policy',
  },
  [PncClassOfBusinessId.CyberLiability]: {
    id: 'cyber-liability',
    title: $localize`Cyber Liability`,
    icon: 'cyber_thin',
    color: 'yellow',
    code: '183',
    type: 'policy',
  },
  [PncClassOfBusinessId.Crime]: {
    id: 'crime',
    title: $localize`Crime`,
    icon: 'crime_thin',
    color: 'red',
    code: '410',
    type: 'policy',
  },
  [PncClassOfBusinessId.PI]: {
    id: 'professional-indemnity',
    title: $localize`Professional Indemnity`,
    icon: 'management_thin',
    color: 'blue',
    code: '175',
    type: 'policy',
  },
  [PncClassOfBusinessId.GroupPersonalAccidentTravel]: {
    id: 'group-personal-accident-travel',
    title: $localize`Group Personal Accident Travel`,
    icon: 'travel_accident_thin',
    color: 'orange',
    code: '456',
    type: 'policy',
  },
  [PncClassOfBusinessId.LegalExpenses]: {
    id: 'legal-expenses',
    title: $localize`Legal Expenses`,
    icon: 'legal_thin',
    color: 'yellow',
    code: '401',
    type: 'policy',
  },
  [PncClassOfBusinessId.DeteriorationOfStock]: {
    id: 'deterioration-of-stock',
    title: $localize`Deterioration of Stock`,
    icon: 'deterioration_stock_thin',
    color: 'green',
    code: '321',
    type: 'policy',
  },
  [PncClassOfBusinessId.AirsideLiability]: {
    id: 'airside-liability',
    title: $localize`Airside Liability`,
    icon: 'airside_liability_thin',
    color: 'blue',
    code: '292',
    type: 'policy',
  },
  [PncClassOfBusinessId.ProductContamination]: {
    id: 'product-contamination',
    title: $localize`Product Contamination`,
    icon: 'contamination_thin',
    color: 'pink',
    code: '132',
    type: 'policy',
  },
  [PncClassOfBusinessId.RiskManagementPractices]: {
    id: 'risk-management-practices',
    title: $localize`Risk Management Practices`,
    icon: 'political_risk_thin',
    color: 'yellow',
    code: '276',
    type: 'policy',
  },
};

export const LSOS_COB_META_DATA: Record<LsosClassOfBusinessId, CobMeta> = {
  [LsosClassOfBusinessId.LSOS]: {
    id: 'lsos',
    title: $localize`:@@cob-466:Master Policy`,
    icon: 'lsos',
    color: 'yellow',
    code: '125',
    type: 'policy',
  },
  [LsosClassOfBusinessId.Cyber]: {
    id: 'cyber',
    title: $localize`Cyber`,
    icon: 'cyber_thin',
    color: 'blue',
    code: '183',
    offline: true,
    type: 'addon',
  },
  [LsosClassOfBusinessId.OfficeGuardian]: {
    id: 'office-guardian',
    title: $localize`Office Guardian`,
    icon: 'management_thin',
    color: 'green',
    code: '428',
    offline: true,
    type: 'addon',
  },
  [LsosClassOfBusinessId.Crime]: {
    id: 'crime',
    title: $localize`Crime`,
    icon: 'crime_thin',
    color: 'red',
    code: '410',
    offline: true,
    type: 'addon',
  },
  [LsosClassOfBusinessId.ManagementLiability]: {
    id: 'management-liability',
    title: $localize`Management Liability`,
    icon: 'management_thin',
    color: 'orange',
    code: '124',
    offline: true,
    type: 'addon',
  },
  [LsosClassOfBusinessId.PISolicitorsExcess]: {
    id: 'pi-solicitors-excess',
    title: $localize`PI Solicitors Excess`,
    icon: 'product_thin',
    color: 'gray',
    code: '126',
    offline: true,
    type: 'addon',
  },
};

export const NORWAY_COB_META_DATA: Record<NorwayClassOfBusinessId, CobMeta> = {
  // Norway - All below are unconfirmed COB IDs
  [NorwayClassOfBusinessId.ProfessionalIndemnity]: {
    id: 'professional-indemnity',
    title: $localize`:@@cob-1541:Professional Indemnity`,
    icon: 'management_thin',
    color: 'green',
    code: '775',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.TravelBusiness]: {
    id: 'travel-business',
    title: $localize`:@@cob-1519:Travel Insurance - Business`,
    description: $localize`When travelling, you will need immediate help in the event of illness, loss of possessions and delays or cancellations. Travel insurance has therefore become an important insurance for work and private life.`,
    icon: 'travel_business_thin',
    color: 'green',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/reiseforsikring-firma.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1519-1:Cancellation of travel`,
        $localize`:@@benefits-1519-2:Delay in the itinerary`,
        $localize`:@@benefits-1519-3:Travel luggage`,
        $localize`:@@benefits-1519-4:Travelsick`,
        $localize`:@@benefits-1519-5:Evacuation`,
        $localize`:@@benefits-1519-6:Car deductible`,
        $localize`:@@benefits-1519-7:Private responsibility`,
        $localize`:@@benefits-1519-8:Legal aid`,
      ],
    },
    code: '476',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.Office]: {
    id: 'office',
    title: $localize`:@@cob-1520:Office Insurance`,
    description: $localize`The Bar Association's office insurance (property insurance) is a combined insurance that covers damage to fixtures and movable property caused by, among other things, fire, water and theft, in addition to other sudden and unforeseen damage.`,
    icon: 'offices_thin',
    color: 'blue',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/kontorforsikring.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1520-1:Fire`,
        $localize`:@@benefits-1520-2:Natural damage according to the Act on natural damage insurance`,
        $localize`:@@benefits-1520-3:Lightning and electrical phenomenon`,
        $localize`:@@benefits-1520-4:Theft, vandalism and robbery`,
        $localize`:@@benefits-1520-5:Damage caused by outflow from the building's pipelines with associated equipment`,
        $localize`:@@benefits-1520-6:Damage caused by ingress of water or other liquid`,
        $localize`:@@benefits-1520-7:Damage to assets directly caused by other sudden and unforeseen damage to buildings`,
        $localize`:@@benefits-1520-8:Demolition and cleanup costs after damage`,
        $localize`:@@benefits-1520-9:Reconstruction of manuscripts, professional drawings, etc`,
        $localize`:@@benefits-1520-10:Policyholder interest in leased equipment`,
        $localize`:@@benefits-1520-11:Glass panes and signs`,
        $localize`:@@benefits-1520-12:Building decoration`,
        $localize`:@@benefits-1520-13:Indirect Damage`,
        $localize`:@@benefits-1520-14:Crime insurance`,
      ],
    },
    code: '477',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.OffWorkAccident]: {
    id: 'off-work-accident',
    title: $localize`:@@cob-1521:Off Work Accident`,
    description: $localize`Most accidents that cause disability and death occur outside the "profession" and working hours. With recreational accident insurance, the employees, and you as a self-employed person, are also covered when you are not at work.`,
    icon: 'accident_health_thin',
    color: 'red',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/fritidsulykkeforsikring.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1521-1:Loss of income incurred`,
        $localize`:@@benefits-1521-2:Future loss of income`,
        $localize`:@@benefits-1521-3:Medical disability (lasting from 1 percent)`,
        $localize`:@@benefits-1521-4:Treatment expenses incurred`,
        $localize`:@@benefits-1521-5:Future additional expenses (in case of permanent medical disability above 50 per cent)`,
        $localize`:@@benefits-1521-6:Death compensation (additional coverage)`,
        $localize`:@@benefits-1521-7:Funeral costs`,
        $localize`:@@benefits-1521-8:Expenses for psychological consultations after traumatic accident events (also applies to accident incidents at work)`,
      ],
    },
    code: '478',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.WorkersCompensation]: {
    id: 'workers-compensation',
    title: $localize`:@@cob-1522:Workers Compensation`,
    description: $localize`Occupational injury insurance provides compensation to employees who are exposed to occupational disease or occupational accidents at the workplace during working hours. The insurance is required by law for all companies with employees.`,
    icon: 'business_accident_health_thin',
    color: 'pink',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/yrkesskadeforsikring.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1522-1:Incurred income loss`,
        $localize`:@@benefits-1522-2:Expenses incurred`,
        $localize`:@@benefits-1522-3:Future loss of income`,
        $localize`:@@benefits-1522-4:Future expenses`,
        $localize`:@@benefits-1522-5:Mental allowance`,
        $localize`:@@benefits-1522-6:Death benefit to cohabitant/spouse`,
        $localize`:@@benefits-1522-7:Death benefit for children`,
        $localize`:@@benefits-1522-8:Funeral expenses`,
      ],
    },
    code: '479',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.FamilyAccident]: {
    id: 'family-accident',
    title: $localize`:@@cob-1523:Family Accident - Private`,
    description: $localize`Family accident insurance provides financial security should you or your spouse/partner or children be exposed to an accident.`,
    icon: 'family_accident_health_thin',
    color: 'yellow',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-privatpersoner/familieulykkeforsikring.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1523-1:<strong>Damage after an accident:</strong><br>Lump-sum payment in case of permanent medical disability after an accident<br>Treatment expenses after an accident`,
        $localize`:@@benefits-1523-2:<strong>Damage after an accident:</strong> Death after an accident One-off payment in case of death after an accident`,
        $localize`:@@benefits-1523-3:<strong>The insurance also covers:</strong> Safe medical assistance`,
      ],
    },
    code: '480',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.FamilyTravel]: {
    id: 'family-travel',
    title: $localize`:@@cob-1524:Travel Insurance - Private`,
    description: $localize`Travel insurance is an important insurance for both work and private life. When travelling, you will need immediate help in the event of illness, loss of possessions and delays or cancellations.`,
    icon: 'travel_thin',
    color: 'blue',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-privatpersoner/reiseforsikring-privat.html',
    misc: {
      benefits: [
        $localize`:@@benefits-1524-1:Cancellation of travel`,
        $localize`:@@benefits-1524-2:Delay in the itinerary`,
        $localize`:@@benefits-1524-3:Travel luggage`,
        $localize`:@@benefits-1524-4:Travelsick`,
        $localize`:@@benefits-1524-5:Evacuation`,
        $localize`:@@benefits-1524-6:Car deductible`,
        $localize`:@@benefits-1524-7:Private responsibility`,
        $localize`:@@benefits-1524-8:Legal aid`,
        $localize`:@@benefits-1524-9:Holiday and leisure travel`,
        $localize`:@@benefits-1524-10:Travel accident`,
        $localize`:@@benefits-1524-11:Extended travel duration (from 3-12 months)`,
        $localize`:@@benefits-1524-12:Extended amount of luggage only for business travel`,
      ],
    },
    code: '481',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.SickLeave]: {
    id: 'sick-leave',
    title: $localize`:@@cob-1525:Sick Leave`,
    description: $localize`Sickness interruption insurance must compensate for loss of income. The Bar Association's offer of sickness absence insurance is specially adapted to legal practice.`,
    icon: 'sick_leave_thin',
    color: 'orange',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/sykeavbruddsforsikring.html',
    misc: {
      healthEvaluationLinks: [
        {
          label: $localize`Partner/Self-employed`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=NAIM',
          type: 'link',
        },
      ],
      benefits: [
        $localize`:@@benefits-1525-1:The insurance covers, up to the selected unemployment benefit amount, loss of turnover in the law firm due to illness or bodily injury affecting the insured during the insurance period`,
        $localize`:@@benefits-1525-2:The compensation is paid with the agreed daily allowance amount for up to 12 months or 24 months, with a deduction for the agreed qualifying period.`,
        $localize`:@@benefits-1525-3:The insurance entitles the insured to 4 consultations of 50 minutes or 4 self-help programs with Tryg Online Psykolog`,
        $localize`:@@benefits-1525-4:External legal assistance with up to NOK 35,000`,
      ],
    },
    code: '482',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.GroupLife]: {
    id: 'group-life',
    title: $localize`:@@cob-1526:Group Life`,
    description: $localize`The offer of group life insurance consists of a death risk insurance and a disability cover (disability capital). The insurance is a reasonable alternative to a corresponding individual life insurance, and is used by many as a debt insurance.`,
    icon: 'group_life_thin',
    color: 'pink',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-privatpersoner/gruppelivsforsikring.html',
    misc: {
      healthEvaluationLinks: [
        {
          label: $localize`:@@he-member:Member`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=DNAM',
          type: 'link',
        },
        {
          label: $localize`Spouse/Partner`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=DNAE',
          type: 'link',
        },
        {
          label: $localize`Partner/Self-employed`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=NAIM',
          type: 'link',
        },
      ],
      benefits: [
        $localize`:@@benefits-1526-1:<strong>Death cover:</strong> The insurance gives the right to a one-off payment in the event of the insured's death`,
        $localize`:@@benefits-1526-2:<strong>Disability capital:</strong> The disability insurance gives the right to a one-off compensation that is paid in the event of permanent incapacity for work as a result of illness or injury occurring during the insurance period. Permanent work disability means that the ability to perform income-generating work (earning capacity) is permanently reduced`,
      ],
    },
    code: '483',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.HealthPrivate]: {
    id: 'health-private',
    title: $localize`:@@cob-2156:Health Insurance - Private`,
    description: $localize`Treatment insurance provides fast and good treatment in the event of injury and illness. The insurance guarantees a consultation within 10 working days and covers 10 physical treatments without requiring a doctor's certificate.`,
    icon: 'health_thin',
    color: 'orange',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-privatpersoner/behandlingsforsikring-individuell.html',
    misc: {
      healthEvaluationLinks: [
        {
          label: $localize`:@@he-member:Member`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=DNAM',
          type: 'link',
        },
        {
          label: $localize`Partner/Self-employed`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=NAIM',
          type: 'link',
        },
      ],
      benefits: [
        $localize`:@@benefits-2156-1:Hospital treatment`,
        $localize`:@@benefits-2156-2:Specialist treatment`,
        $localize`:@@benefits-2156-3:Cancer treatment`,
        $localize`:@@benefits-2156-4:Physical treatments`,
        $localize`:@@benefits-2156-5:Psychologist`,
        $localize`:@@benefits-2156-6:Psychological First Aid`,
        $localize`:@@benefits-2156-7:Medicines`,
        $localize`:@@benefits-2156-8:Technical aids after surgery`,
        $localize`:@@benefits-2156-9:Travel expenses`,
        $localize`:@@benefits-2156-10:Subsistence expenses`,
        $localize`:@@benefits-2156-11:Travel companion`,
        $localize`:@@benefits-2156-12:Second Opinion`,
        $localize`:@@benefits-2156-13:Rehabilitation`,
        $localize`:@@benefits-2156-14:Drug and Gambling Weaning`,
        $localize`:@@benefits-2156-15:Warranty period`,
        $localize`:@@benefits-2156-16:Social security medical assistance`,
      ],
    },
    code: '812',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.ProfessionalLiability]: {
    id: 'professional-liability',
    title: 'Profesjonsansvarsforsikring for advokatvirksomhet',
    description:
      'Formueskadeforsikring (advokatvirksomhet), sum per skadetilfelle',
    icon: 'business_interruption_thin',
    color: 'yellow',
    code: '',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.PropertyDamage]: {
    id: 'property-damage',
    title: 'Profesjonsansvarsforsikring eiendomsmegling',
    description:
      'Profesjonsansvarsforsikring eiendomsmegling, forsikringssum per skade og per år',
    icon: 'property_thin',
    color: 'green',
    code: '',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.DirectorsLiability]: {
    id: 'directors-liability',
    title: 'Styreansvarsforsikring for eksterne styreverv',
    description:
      'Styreansvarsforsikring utenfor eget parnerskap /advokatfirma, forsikringssum pr. skade og per år',
    icon: 'management_thin',
    color: 'blue',
    code: '',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.Crime]: {
    id: 'crime',
    title: 'Kriminalitetsforsikring',
    description: 'Kriminalitetsforsikring, forsikringssum pr. skade og per år',
    icon: 'crime_thin',
    color: 'orange',
    code: '',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.Cyber]: {
    id: 'cyber',
    title: 'Dataangrepsforsikring (first party cyber insurance)',
    description: 'Dataangrepsforsikring (first party cyber insurance)',
    icon: 'cyber_thin',
    color: 'pink',
    code: '',
    type: 'policy',
  },
  [NorwayClassOfBusinessId.HealthBusiness]: {
    id: 'health-business',
    title: $localize`:@@cob-1555:Health Insurance - Business`,
    description: $localize`Treatment insurance provides fast and good treatment in the event of injury and illness. The insurance guarantees a consultation within 10 working days and covers 10 physical treatments without requiring a doctor's certificate.`,
    icon: 'business_health_thin',
    color: 'yellow',
    externalLink:
      'https://forsikring.advokatforeningen.no/for-advokatfirma/behandlingsforsikring-firma.html',
    misc: {
      healthEvaluationLinks: [
        {
          label: $localize`:@@he-member:Member`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=DNAM',
          type: 'link',
        },
        {
          label: $localize`Partner/Self-employed`,
          href: 'https://helseweb.tryg.no/index.html?organization_id=05&prodno=540&internalExternal=E&partner=002&skjematype=NAIM',
          type: 'link',
        },
      ],
      benefits: [
        $localize`:@@benefits-1555-1:Hospital treatment`,
        $localize`:@@benefits-1555-2:Specialist treatment`,
        $localize`:@@benefits-1555-3:Cancer treatment`,
        $localize`:@@benefits-1555-4:Physical treatments`,
        $localize`:@@benefits-1555-5:Psychologist`,
        $localize`:@@benefits-1555-6:Psychological First Aid`,
        $localize`:@@benefits-1555-7:Medicines`,
        $localize`:@@benefits-1555-8:Technical aids after surgery`,
        $localize`:@@benefits-1555-9:Travel expenses`,
        $localize`:@@benefits-1555-10:Subsistence expenses`,
        $localize`:@@benefits-1555-11:Travel companion`,
        $localize`:@@benefits-1555-12:Second Opinion`,
        $localize`:@@benefits-1555-13:Rehabilitation`,
        $localize`:@@benefits-1555-14:Drug and Gambling Weaning`,
        $localize`:@@benefits-1555-15:Warranty period`,
        $localize`:@@benefits-1555-16:Social security medical assistance`,
      ],
    },
    code: '811',
    type: 'policy',
  },
};

// export function showHealthEvaluation(cobId: number | undefined) {
//   return (
//     includes(
//       [
//         NorwayClassOfBusinessId.HealthBusiness,
//         NorwayClassOfBusinessId.HealthPrivate,
//         NorwayClassOfBusinessId.GroupLife,
//         NorwayClassOfBusinessId.SickLeave,
//       ],
//       cobId
//     ) || false
//   );
// }

// All below are unconfirmed COB IDs
export const ACCA_COB_META_DATA: Record<
  AccaClassOfBusinessId,
  CobMeta<AccaClassOfBusinessId>
> = {
  /** @deprecated: this now falls under 2279 (ACCA_OVER) */
  [AccaClassOfBusinessId.ProfessionalIndemnity]: {
    id: 'professional-indemnity',
    title: $localize`:@@cob-4001:Professional Indemnity`,
    icon: 'management_thin',
    color: 'green',
    code: '179',
    offline: false,
    type: 'policy',
  },
  [AccaClassOfBusinessId.EmployersPublicAndProductLiability]: {
    id: 'employers-public-and-product-liability',
    title: $localize`:@@cob-4001:Employers’ and/or Public and Product Liability`,
    icon: 'business_accident_health_thin',
    color: 'green',
    code: '',
    offline: false,
    type: 'bolton',
  },
  [AccaClassOfBusinessId.EmployersLiability]: {
    id: 'employers-liability',
    title: $localize`:@@cob-4001:Employers’ Liability`,
    description: $localize`Employers’ Liability is a legal requirement if your business employs one or more people, you can be fined £2,500 for every day you are not properly insured. Your firm is responsible for the safeguarding of the general public, you could be held liable for any accidental bodily injury or property damage arising from your business activities. Protect yourself with our policy.`,
    icon: 'business_accident_health_thin',
    color: 'orange',
    mapTo: AccaClassOfBusinessId.EmployersPublicAndProductLiability,
    code: '111',
    offline: false,
    type: 'bolton',
  },
  [AccaClassOfBusinessId.PublicAndProductLiability]: {
    id: 'public-and-product-liability',
    title: $localize`:@@cob-4001:Public and Product Liability`,
    description: $localize`Public liability insurance covers you against claims made by members of the public including your clients, who may have suffered damage or injury to themselves or their property as a result of your business.`,
    icon: 'accident_thin',
    color: 'yellow',
    mapTo: AccaClassOfBusinessId.EmployersPublicAndProductLiability,
    code: '112',
    offline: false,
    type: 'bolton',
  },
  [AccaClassOfBusinessId.GroupPersonalAccident]: {
    id: 'group-personal-accident',
    title: $localize`:@@cob-4002:Group Personal Accident`,
    description: $localize`The Group Personal Accident section covers your Directors, Partners, or Officers in the event of an incident resulting in death or disablement, including loss of limbs, sight, speech, or hearing. The policy will pay out a lump sum which can be used to support the Director, Partner, or Officer or their beneficiaries or cover a replacement member of staff.`,
    icon: 'accident_health_thin',
    color: 'pink',
    code: '452',
    offline: false,
    type: 'bolton',
  },
  [AccaClassOfBusinessId.ManagementLiability]: {
    id: 'management-liability',
    title: $localize`:@@cob-4003:Management Liability (D&O)`,
    description: $localize`If your firm runs into difficulties or your duty of care is questioned, you could find yourself and your personal assets firmly in the firing line.`,
    icon: 'accident_health_thin',
    color: 'blue',
    code: '346',
    offline: false,
    type: 'bolton',
  },
  [AccaClassOfBusinessId.Cyber]: {
    id: 'cyber',
    title: $localize`:@@cob-4004:Cyber`,
    description: $localize`Hackers see Accountants as aggregators of personal and sensitive data which makes you a prime target for an attack – protect yourself with our policy.`,
    icon: 'cyber_thin',
    color: 'green',
    offline: true,
    code: '183',
    type: 'bolton',
  },
  [AccaClassOfBusinessId.OfficeCombined]: {
    id: 'office-combined',
    title: $localize`:@@cob-4005:Office Combined`,
    description: $localize`Your office is not only a place of employment it generates profits for owners and partners so what if you lost everything you needed to keep your business running.`,
    icon: 'offices_thin',
    color: 'red',
    offline: true,
    code: '477',
    type: 'bolton',
  },
};

export const ACCA_OVER_COB_META_DATA: Record<
  AccaOverClassOfBusinessId,
  CobMeta<AccaOverClassOfBusinessId>
> = {
  [AccaOverClassOfBusinessId.NationalAccountants]: {
    id: 'national-accountants',
    title: $localize`PI Accountants (National)`,
    icon: 'management_thin',
    color: 'green',
    code: '158',
    type: 'policy',
  },
  [AccaOverClassOfBusinessId.NationalAccountantsAcca]: {
    id: 'national-accountants',
    title: $localize`PI Accountants ACCA`,
    icon: 'management_thin',
    color: 'green',
    code: '179',
    type: 'policy',
    misc: {
      limitOfIndemnityBasis: $localize`Any one Claim or loss including Claimants’ costs and expenses, Defence Costs in addition, but limited to in the aggregate for the Period of Insurance, including Defence Costs, in respect of Claims or losses arising out of a Cyber Act, which sum shall be included in and is not additional to the Limit of Indemnity for any one Claim or loss.`,
      excessBasis: $localize`Each and every claim excluding defence costs and subject to an aggregated cap of three times the excess for the total of all excesses. Note on occasion for high risk work Arch may specify that the aggregated cap should not apply.`,
      legalExpensesLimitOfIndemnity: `£250,000`,
      legalExpensesLimitOfIndemnityBasis: $localize`£250,000 for items 1 Employment to 10 Personal Injury (inclusive) and 12 Contract and Debt Recovery\n£25,000 for item 11 Executive Suite and 13 Crisis Communication\n£1,000,000 aggregate limit under insured event 2 Employment Compensation Awards`,
      legalExpensesExcess: $localize`£Nil`,
    },
  },
  [AccaOverClassOfBusinessId.ProfessionsAccountants]: {
    id: 'national-accountants',
    title: $localize`PI Accountants (Professions)`,
    icon: 'management_thin',
    color: 'green',
    code: '158',
    type: 'policy',
  },
};

export const CONSTRUCTION_COB_META_DATA: Record<
  ConstructionClassOfBusinessId,
  CobMeta<ConstructionClassOfBusinessId>
> = {
  [ConstructionClassOfBusinessId.ProfessionsArchitects]: {
    id: 'architects-professions',
    title: $localize`PI Architects (Professions)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '163',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalArchitects]: {
    id: 'architects-national',
    title: $localize`PI Architects (National)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '163',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.ProfessionsEngineers]: {
    id: 'architects-professions',
    title: $localize`PI Engineers (Professions)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '167',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalEngineers]: {
    id: 'engineers-national',
    title: $localize`PI Engineers (National)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '167',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.ProfessionsDesignAndConstruction]: {
    id: 'design-construction-professions',
    title: $localize`PI Design and Construction (Professions)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '176',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.ProfessionsPropertyManagers]: {
    id: 'property-managers-professions',
    title: $localize`PI Property Managers (Professions)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '236',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalDesignAndConstruction]: {
    id: 'design-construction-national',
    title: $localize`Design and Construction (National)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '176',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalPropertyManagers]: {
    id: 'property-managers-national',
    title: $localize`PI Property Managers (National)`,
    icon: 'management_thin',
    color: 'green',
    misc: {},
    code: '236',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.ProfessionsSurveyors]: {
    id: 'surveyors-professions',
    title: $localize`PI Surveyors (Professions)`,
    icon: 'product_thin',
    color: 'blue',
    code: '166',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalSurveyors]: {
    id: 'surveyors-national',
    title: $localize`PI Surveyors (National)`,
    icon: 'product_thin',
    color: 'blue',
    code: '166',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.ProfessionsEstateAgent]: {
    id: 'estate-agent-professions',
    title: $localize`PI Estate Agent (Professions)`,
    icon: 'product_thin',
    color: 'blue',
    code: '172',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalEstateAgent]: {
    id: 'estate-agent-national',
    title: $localize`PI Estate Agent (National)`,
    icon: 'product_thin',
    color: 'blue',
    code: '172',
    type: 'policy',
  },
  [ConstructionClassOfBusinessId.NationalSurveyorsRics]: {
    id: 'surveyors-rics',
    title: $localize`PI Surveyors RICS`,
    icon: 'product_thin',
    color: 'blue',
    code: '280',
    type: 'policy',
  },
};

export const REGULATORY_COB_META_DATA: Record<
  RegulatoryClassOfBusinessId,
  CobMeta
> = {
  [RegulatoryClassOfBusinessId.AccountantsIfa]: {
    id: 'pi-ifa',
    title: $localize`PI E&O IFA`,
    icon: 'product_thin',
    color: 'blue',
    code: '165',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryFinancialServices]: {
    id: 'pi-financial-services',
    title: $localize`PI Financial Services`,
    icon: 'product_thin',
    color: 'orange',
    code: '184',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryInsuranceBrokers]: {
    id: 'pi-brokers',
    title: $localize`PI Insurance Brokers`,
    icon: 'product_thin',
    color: 'green',
    code: '164',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryLloyds]: {
    id: 'lloyds-brokers',
    title: $localize`PI Lloyds Insurance Brokers`,
    icon: 'product_thin',
    color: 'yellow',
    code: '185',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryMortgageAdvisors]: {
    id: 'pi-mortgage-advisors',
    title: $localize`PI Mortgage Advisors`,
    icon: 'product_thin',
    color: 'pink',
    code: '186',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryUnderwriterAgentsAndManagers]: {
    id: 'pi-underwriter-agents-managers',
    title: $localize`PI Underwriter Agents & Managers`,
    icon: 'product_thin',
    color: 'red',
    code: '191',
    type: 'policy',
  },
  [RegulatoryClassOfBusinessId.RegulatoryEnOIfa]: {
    id: 'pi-ifa',
    title: $localize`PI E&O IFA`,
    icon: 'product_thin',
    color: 'blue',
    code: '165',
    type: 'policy',
  },
};

export const ENW_COB_META_DATA: Record<EnwClassOfBusinessId, CobMeta> = {
  [EnwClassOfBusinessId.PI]: {
    id: 'pi',
    title: $localize`Professional Indemnity`,
    department: $localize`National PI Solicitors E&W (1232)`,
    icon: 'product_thin',
    color: 'blue',
    code: '125',
    type: 'policy',
  },
  [EnwClassOfBusinessId.PI2]: {
    id: 'pi',
    title: $localize`Professional Indemnity`,
    department: $localize`Professions PI Solicitors & Accountants (1031)`,
    icon: 'product_thin',
    color: 'blue',
    code: '125',
    type: 'policy',
  },
};

export const MID_MARKET_OFFLINE_COB_META_DATA: Record<
  MidMarketOfflineClassOfBusinessId,
  CobMeta
> = {
  [MidMarketOfflineClassOfBusinessId.EmployersLiability]: {
    id: 'employers-liability',
    title: $localize`:@@cob-11001:Employers’ Liability`,
    description: $localize`Employers’ Liability is a legal requirement if your business employs one or more people, you can be fined £2,500 for every day you are not properly insured. Your firm is responsible for the safeguarding of the general public, you could be held liable for any accidental bodily injury or property damage arising from your business activities. Protect yourself with our policy.`,
    icon: 'business_accident_health_thin',
    color: 'orange',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.PublicAndProductLiability]: {
    id: 'public-and-product-liability',
    title: $localize`:@@cob-11002:Public and Product Liability`,
    description: $localize`Public liability insurance covers you against claims made by members of the public including your clients, who may have suffered damage or injury to themselves or their property as a result of your business.`,
    icon: 'accident_thin',
    color: 'yellow',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.ManagementLiability]: {
    id: 'management-liability',
    title: $localize`:@@cob-11003:Management Liability (D&O)`,
    description: $localize`If your firm runs into difficulties or your duty of care is questioned, you could find yourself and your personal assets firmly in the firing line.`,
    icon: 'accident_health_thin',
    color: 'blue',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.GroupPersonalAccident]: {
    id: 'group-personal-accident',
    title: $localize`:@@cob-11004:Group Personal Accident`,
    description: $localize`The Group Personal Accident section covers your Directors, Partners, or Officers in the event of an incident resulting in death or disablement, including loss of limbs, sight, speech, or hearing. The policy will pay out a lump sum which can be used to support the Director, Partner, or Officer or their beneficiaries or cover a replacement member of staff.`,
    icon: 'accident_health_thin',
    color: 'pink',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.Cyber]: {
    id: 'cyber',
    title: $localize`:@@cob-11005:Cyber`,
    description: $localize`Hackers see Accountants as aggregators of personal and sensitive data which makes you a prime target for an attack – protect yourself with our policy.`,
    icon: 'cyber_thin',
    color: 'green',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.OfficeCombined]: {
    id: 'office-combined',
    title: $localize`:@@cob-11006:Office Combined`,
    description: $localize`Your office is not only a place of employment it generates profits for owners and partners so what if you lost everything you needed to keep your business running.`,
    icon: 'offices_thin',
    color: 'red',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.Crime]: {
    id: 'crime',
    title: $localize`:@@cob-11007:Crime`,
    description: $localize`If an employee steals money, securities, or property from you or a client, a crime policy reimburses you. A crime policy can also provide coverage from illegal acts of third parties, such as computer fraud, wire transfer fraud, and robbery/burglary that result in a loss.`,
    icon: 'crime_thin',
    color: 'orange',
    offline: true,
    code: '',
    type: 'bolton',
  },
  [MidMarketOfflineClassOfBusinessId.EmployeeBenefits]: {
    id: 'employee-benefit',
    title: $localize`:@@cob-11008:Employee Benefits`,
    description: $localize`Your staff are your single biggest cost and resource.  In a top-end service profession where reputation and trust are critical to success, retaining and rewarding your best people is vitally important. Lockton Benefits UK specialises in employee benefits, particularly group programmes such as medical and dental insurance, death in service and disability cover.`,
    icon: 'client_thin',
    color: 'yellow',
    offline: true,
    code: '',
    type: 'bolton',
  },
};

export const COB_META_DATA_DEFAULT: CobMetaDictionary = defaults(
  PNC_COB_META_DATA,
  LSOS_COB_META_DATA,
  NORWAY_COB_META_DATA,
  ACCA_COB_META_DATA,
  CONSTRUCTION_COB_META_DATA,
  REGULATORY_COB_META_DATA,
  ENW_COB_META_DATA,
  MID_MARKET_OFFLINE_COB_META_DATA,
  ACCA_OVER_COB_META_DATA
);

export type CobMetaDictionary = Record<number, CobMeta>;

export const COB_META_DATA = new InjectionToken<CobMetaDictionary>(
  'Class of business meta data dictionary'
);

export function provideCobMeta(cobMeta: CobMetaDictionary) {
  return [{ provide: COB_META_DATA, useValue: cobMeta }, CobMetaService];
}

export function getClassOfBusinessMetaById(
  id: string,
  partner?: Partners
): CobMeta | undefined {
  return find(getMetaForPartner(partner), { id });
}

export function getClassOfBusinessMeta(
  cob: ClassOfBusinessId
): CobMeta | undefined {
  return COB_META_DATA_DEFAULT[cob];
}

export function getMetaForPartner(
  partner?: Partners
): Partial<Record<ClassOfBusinessId, CobMeta>> {
  switch (partner) {
    case Partners.LSOS:
      return LSOS_COB_META_DATA;

    case Partners.NORWAY:
    case Partners.NORWAY_PI:
      return NORWAY_COB_META_DATA;

    case Partners.ACCA:
      return ACCA_COB_META_DATA;

    case Partners.PNC:
      return PNC_COB_META_DATA;

    case Partners.REGULATORY:
      return REGULATORY_COB_META_DATA;

    case Partners.CONSTRUCTION:
      return CONSTRUCTION_COB_META_DATA;

    case Partners.ACCA_OVER:
      return ACCA_OVER_COB_META_DATA;

    default:
      return COB_META_DATA_DEFAULT;
  }
}

interface UseCobMetaOptionsPipeProps {
  on: boolean;
  emptyList?: FormlySelectOption[];
  parsers?: Array<(...args: any) => any>;
  displayWith: (cobMeta: CobMeta | undefined) => string;
}

const DEFAULT_OPTIONS = {
  emptyList: [],
  parsers: [identity],
  displayWith: (cobMeta: CobMeta | undefined) => cobMeta?.title || '',
};

export class CobMetaOptionsPipeExtension implements FormlyExtension {
  constructor(private cobMetaService: CobMetaService) {}

  prePopulate(field: FormlyFieldConfig) {
    const props = field.props || {};
    const _propOptions: UseCobMetaOptionsPipeProps = isBoolean(
      props['useCobMetaOptionsPipe']
    )
      ? { on: props['useCobMetaOptionsPipe'] }
      : props['useCobMetaOptionsPipe'] || { on: false };
    const propOptions = defaults(_propOptions, DEFAULT_OPTIONS);

    if (!propOptions.on) {
      return;
    }

    field.expressions = {
      ...field.expressions,
      'props.options': (field) => {
        const _options = get(field, 'props._options');

        const _ids$ =
          _options instanceof Observable
            ? _options.pipe(distinctUntilChanged())
            : of(_options || []);

        const options$ =
          props.options instanceof Observable
            ? props.options.pipe(
                distinctUntilChanged((prev, cur) => !isEqual(prev, cur))
              )
            : of(props.options || []);

        if (!field.props) return;

        field.props.options = combineLatest([_ids$, options$]).pipe(
          rxMap(([_ids, options]) => {
            const ids = map(options, 'value');

            if (_ids.length === 0) {
              return propOptions.emptyList || [];
            }

            const all = map(_ids, (id) => {
              const cobMeta = this.cobMetaService.getClassOfBusinessMeta(id);
              return {
                label: propOptions.displayWith(cobMeta),
                value: id,
                meta: cobMeta,
              };
            });

            const parsed = flow(propOptions.parsers as any)(all);
            const parsedIds = map(parsed, 'value');
            const isDifferent = xor(parsedIds, ids).length > 0;

            if (!field.props || !isDifferent) return options;

            return parsed.length > 0 ? parsed : propOptions.emptyList || [];
          }),
          distinctUntilChanged((prev, cur) => !isEqual(prev, cur)),
          shareReplay(1)
        );
      },
    };
  }
}

export function registerCobMetaOptionsPipeExtension(
  cobMetaService: CobMetaService
) {
  return {
    extensions: [
      {
        name: 'cob-meta-options-pipe',
        extension: new CobMetaOptionsPipeExtension(cobMetaService),
      },
    ],
  };
}
